import React, { useState, useEffect, useReducer, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Clock,
  Eye,
  Heart,
  Home,
  Menu,
  Mic2,
  Settings,
  ShoppingBag,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import LoopCard from '../components/LoopCard';
import Filters from '../components/Filters';
import PacksLibrary from '../components/PacksLibrary';
import PizzulCoach from '../components/PizzulCoach';
import { usePlayer } from '../contexts/PlayerContext';
import { useFavorites } from '../hooks/useFavorites';
import { usePreview } from '../contexts/PreviewContext';

import { API_URL } from '../utils/api';
import { INSTRUMENT_GROUPS, GROUP_HEX } from '../utils/groupColors';
import { initialLibraryView, libraryViewReducer } from '../utils/libraryViewState';
import { buildLoopQuery } from '../utils/loopQuery';
import { createFitsPager, mergeFitsPage } from '../utils/fitsPager';
import { coachSteps } from '../utils/coachSteps';
import { vibeStyle } from '../utils/vibeStyle';
// The tempo slider's full span. Sitting at this range means "All tempos", so
// it must NOT be sent as a filter: a loop with no BPM fails any numeric range
// test, and 736 of 2,149 published loops have no BPM yet. Sending the default
// silently hid a third of the library from students on every single request.
const BPM_RANGE_DEFAULT = [60, 200];
const isDefaultBpmRange = (r) =>
  !r || (r[0] === BPM_RANGE_DEFAULT[0] && r[1] === BPM_RANGE_DEFAULT[1]);

const PIZZLE_IMAGE = '/images/pizzle/2lnbap9h_Pizzle_Chill_-_L.png';
const PIZZLE_SATISFIED = '/images/pizzle/pog35nvs_Pizzle_Satisfied_-_L.png';

// Crate Header Button Component
const CrateHeaderButton = () => {
  const { crate, setCrateOpen } = usePreview();
  
  return (
    <Button
      data-testid="header-crate-btn"
      onClick={() => setCrateOpen(true)}
      variant="outline"
      size="sm"
      className="rounded-full border-accent/50 text-accent hover:bg-accent/10 relative"
      title="Crate"
    >
      <ShoppingBag className="w-4 h-4" />
      <span className="hidden sm:inline ml-1.5 font-semibold">Crate</span>
      {crate.length > 0 && (
        <span className="ml-1 bg-black text-accent text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center border border-accent">
          {crate.length}
        </span>
      )}
    </Button>
  );
};

const Library = () => {
  const [libraryView, dispatchLibraryView] = useReducer(
    libraryViewReducer,
    null,
    () => ({ view: initialLibraryView(), pack: null, packLoops: null, packTab: 'Melodies' })
  );
  const [loops, setLoops] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [taxonomy, setTaxonomy] = useState(null);
  const [vibePacks, setVibePacks] = useState([]);
  const [activeVibePack, setActiveVibePack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    mood_category: '',
    mood_tags: '',
    style_tags: '',
    vibe: '',
    key: '',  // Musical key filter
    energy: null,
    bpm_range: BPM_RANGE_DEFAULT,
    vibe_pack: '',
    instrument_group: ''
  });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  
  const { currentLoop, isPlaying, recentLoops, clearRecents, stop, trackVibeClick, playLoop } = usePlayer();
  const { favorites, isFavorite, toggleFavorite, clearFavorites } = useFavorites();
  const { sessionBpm, tracks } = usePreview();
  // The pack a kid left when Pizzul sent them to All loops for a missing
  // instrument, so they can get back to it.
  const [returnPack, setReturnPack] = useState(null);
  const [handoffBpm, setHandoffBpm] = useState(null);
  const [handoffPackName, setHandoffPackName] = useState(null);
  const fitsBpm = Number.isInteger(sessionBpm) ? sessionBpm : handoffBpm;
  const [proDetails, setProDetails] = useState(() => { try { return localStorage.getItem('sp_pro_details') === 'true'; } catch (_) { return false; } });
  const [fitsMyBeat, setFitsMyBeat] = useState(() => Number.isInteger(sessionBpm));
  const [loopError, setLoopError] = useState(false);
  const [partialLoopError, setPartialLoopError] = useState(false);
  const requestToken = useRef(0);
  // Fits my beat turns itself on when a beat first exists, then stays the kid's call.
  const hadBeat = useRef(Number.isInteger(sessionBpm));
  useEffect(() => {
    const hasBeat = Number.isInteger(sessionBpm);
    if (hasBeat && !hadBeat.current) setFitsMyBeat(true);
    if (hasBeat) setHandoffBpm(null);
    hadBeat.current = hasBeat;
  }, [sessionBpm]);
  const fitsPager = useRef(null);
  
  // State for showing favorites section
  const [showFavorites, setShowFavorites] = useState(false);

  const setLibraryView = (view) => {
    if (view === 'packs') setReturnPack(null);
    dispatchLibraryView({ type: 'VIEW', view });
    try {
      localStorage.setItem('sp_library_view', view);
    } catch (_) {
      // Storage is optional, such as in private browsing.
    }
  };

  const openPack = (pack) => {
    setReturnPack(null);
    dispatchLibraryView({ type: 'OPEN_PACK', pack });
    // Pack opens feed the admin pack stats.
    fetch(`${API_URL}/api/sound-packs/${pack.pack_id}/select`, { method: 'POST' }).catch(() => {});
  };

  const handoffToAllLoops = (group, packTempo = null) => {
    setFilters((previous) => ({
      ...previous,
      instrument_group: group,
    }));
    if (!Number.isInteger(sessionBpm) && Number.isInteger(packTempo)) {
      setHandoffBpm(packTempo);
      setHandoffPackName(libraryView.pack?.name || null);
    }
    if (libraryView.pack) setReturnPack(libraryView.pack);
    setFitsMyBeat(true);
    setLibraryView('all');
  };

  // Back into the pack, on the given group or the next instrument the beat
  // still needs that this pack actually has.
  const backToPack = (group = null) => {
    const pack = returnPack;
    if (!pack) return;
    const has = (name) => (pack.groupCounts?.[name] || 0) > 0;
    const next = coachSteps(tracks).current;
    const tab = group || (INSTRUMENT_GROUPS.includes(next) && has(next) ? next : INSTRUMENT_GROUPS.find(has) || 'Melodies');
    setReturnPack(null);
    setHandoffBpm(null);
    setHandoffPackName(null);
    dispatchLibraryView({ type: 'OPEN_PACK', pack });
    dispatchLibraryView({ type: 'PACK_TAB', tab });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const selectCoachGroup = (group) => {
    if (libraryView.view === 'pack') {
      dispatchLibraryView({ type: 'PACK_TAB', tab: group });
    } else if (returnPack && (returnPack.groupCounts?.[group] || 0) > 0) {
      backToPack(group);
    } else {
      setFilters((previous) => ({ ...previous, instrument_group: group }));
      setLibraryView('all');
    }
  };

  useEffect(() => {
    const select = (event) => selectCoachGroup(event.detail);
    window.addEventListener('sp-select-group', select);
    return () => window.removeEventListener('sp-select-group', select);
  });
  
  // Reset to vibe selection mode
  const resetToVibes = () => {
    stop(); // Stop any playing audio
    clearRecents(); // Clear recents
    clearFilters({ keepTab: false }); // Clear filters
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Fetch vibe packs
  useEffect(() => {
    const fetchVibePacks = async () => {
      try {
        const response = await fetch(`${API_URL}/api/vibe-packs`);
        if (response.ok) {
          const data = await response.json();
          setVibePacks(data);
        }
      } catch (error) {
        console.error('Failed to fetch vibe packs:', error);
      }
    };
    fetchVibePacks();
  }, []);
  
  // Fetch themed packs (published only)
  // Apply vibe pack filters
  const applyVibePack = (pack) => {
    if (activeVibePack?.id === pack.id) {
      // Deselect - clear filters
      clearFilters();
      setActiveVibePack(null);
    } else {
      setActiveVibePack(pack);
      // Track vibe click
      trackVibeClick(pack.name);
      // Use vibe_pack param to get both directly tagged AND filter-matched loops
      setFilters(prev => ({
        ...prev,
        search: '',
        mood_category: '',
        style_tags: '',
        vibe: '',
        energy: null,
        bpm_range: BPM_RANGE_DEFAULT,
        vibe_pack: pack.name  // Pass vibe pack name for direct + filter matching
      }));
    }
  };
  
  // Fetch taxonomy
  useEffect(() => {
    const fetchTaxonomy = async () => {
      try {
        const response = await fetch(`${API_URL}/api/taxonomy`);
        if (response.ok) {
          const data = await response.json();
          setTaxonomy(data);
        }
      } catch (error) {
        console.error('Failed to fetch taxonomy:', error);
      }
    };
    fetchTaxonomy();
  }, []);
  
  // Pagination state
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const LOOPS_PER_PAGE = 75;
  
  // Fetch loops. Fits mode pages every eligible tempo stream under one token.
  useEffect(() => {
    const fetchInitialLoops = async () => {
      const token = ++requestToken.current;
      setLoading(true);
      setPage(0);
      setLoopError(false);
      setPartialLoopError(false);
      const beatBpm = Number.isInteger(fitsBpm) ? fitsBpm : null;
      const useFits = fitsMyBeat && beatBpm;
      fitsPager.current = useFits ? createFitsPager(beatBpm, LOOPS_PER_PAGE) : null;
      try {
        const streams = useFits ? fitsPager.current.streams : [{ tempo: null, skip: 0 }];
        const pages = await Promise.all(streams.map(async (stream) => { try { const response = await fetch(`${API_URL}/api/loops?${buildLoopQuery(filters, { limit: LOOPS_PER_PAGE, skip: stream.skip, shuffle: !useFits, tempo: stream.tempo }).toString()}`); if (!response.ok) throw new Error(); return { tempo: stream.tempo, data: await response.json() }; } catch (error) { return { tempo: stream.tempo, data: [], error }; } }));
        if (token !== requestToken.current) return;
        if (useFits) { const result = mergeFitsPage(fitsPager.current, pages); setLoops(result.loops); setHasMore(result.hasMore); setLoopError(result.allFailed); setPartialLoopError(result.failures > 0 && !result.allFailed); }
        else { const pageResult = pages[0]; if (pageResult.error) setLoopError(true); else { setLoops(pageResult.data); setHasMore(pageResult.data.length >= LOOPS_PER_PAGE); } }
      } catch (error) {
        if (token === requestToken.current) setLoopError(true);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialLoops();
  }, [filters, fitsMyBeat, fitsBpm]);
  
  // Load more handler - completely separate from initial fetch
  const loadMoreLoops = async () => {
    const token = ++requestToken.current;
    const nextPage = page + 1;
    setPartialLoopError(false);
    try {
      const pager = fitsPager.current;
      const streams = pager ? pager.streams.filter((stream) => stream.hasMore) : [{ tempo: null, skip: nextPage * LOOPS_PER_PAGE }];
      const pages = await Promise.all(streams.map(async (stream) => { try { const response = await fetch(`${API_URL}/api/loops?${buildLoopQuery(filters, { limit: LOOPS_PER_PAGE, skip: stream.skip, shuffle: false, tempo: stream.tempo }).toString()}`); if (!response.ok) throw new Error(); return { tempo: stream.tempo, data: await response.json() }; } catch (error) { return { tempo: stream.tempo, data: [], error }; } }));
      if (token !== requestToken.current) return;
      if (pager) { const result = mergeFitsPage(pager, pages); setLoops((previous) => [...previous, ...result.loops]); setHasMore(result.hasMore); setPartialLoopError(result.failures > 0); }
      else if (pages[0].error) setPartialLoopError(true); else { setLoops((previous) => [...previous, ...pages[0].data]); setPage(nextPage); setHasMore(pages[0].data.length >= LOOPS_PER_PAGE); }
    } catch (error) {
      if (token === requestToken.current) setPartialLoopError(true);
    }
  };
  
  // Fetch suggestions when a loop is playing
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!currentLoop) {
        setSuggestions([]);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/loops/${currentLoop.loop_id}/suggestions`);
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data);
        }
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
      }
    };
    
    fetchSuggestions();
  }, [currentLoop]);
  
  // Clears keep the instrument tab; it only moves when a kid picks one (or Home resets everything).
  const clearFilters = ({ keepTab = true } = {}) => {
    setFilters((previous) => ({
      search: '',
      mood_category: '',
      mood_tags: '',
      style_tags: '',
      vibe: '',
      key: '',
      energy: null,
      bpm_range: BPM_RANGE_DEFAULT,
      vibe_pack: '',
      instrument_group: keepTab ? previous.instrument_group : ''
    }));
    setActiveVibePack(null);
  };
  
  return (
    <div data-testid="library-page" className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img 
              src={PIZZLE_IMAGE} 
              alt="Pizzul" 
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <h1 className="font-heading font-bold text-xl sm:text-2xl">Studio Playground</h1>
              <p className="text-xs text-muted-foreground hidden sm:block">Let The Beat Build</p>
            </div>
          </div>

          <nav aria-label="Browse" className="order-3 sm:order-none flex rounded-full border border-white/10 bg-card p-1">
            <button
              type="button"
              aria-pressed={libraryView.view !== 'all'}
              onClick={() => setLibraryView('packs')}
              className={`min-h-10 rounded-full px-4 text-sm font-bold ${libraryView.view !== 'all' ? 'bg-white text-black' : ''}`}
            >
              Themed Packs
            </button>
            <button
              type="button"
              aria-pressed={libraryView.view === 'all'}
              onClick={() => setLibraryView('all')}
              className={`min-h-10 rounded-full px-4 text-sm font-bold ${libraryView.view === 'all' ? 'bg-white text-black' : ''}`}
            >
              All loops
            </button>
          </nav>
          
          <div className="flex items-center gap-2">
            {/* Mobile Filter Toggle */}
            <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
              <SheetTrigger asChild>
                <Button 
                  data-testid="mobile-filters-btn"
                  variant="outline" 
                  size="icon" 
                  className="lg:hidden rounded-full border-white/20"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] bg-background border-white/10">
                <div className="mt-6">
                  <Filters
                    filters={filters}
                    setFilters={setFilters}
                    taxonomy={taxonomy}
                    onClear={() => clearFilters()}
                  />
                </div>
              </SheetContent>
            </Sheet>
            
            {/* Home button, shown exactly when the vibe section is hidden, so the
                two conditions stay each other's inverse rather than drifting. */}
            {currentLoop && (
              <Button
                data-testid="show-vibes-btn"
                onClick={resetToVibes}
                variant="outline"
                size="sm"
                className="rounded-full border-primary/50 text-primary hover:bg-primary/10"
              >
                <Home className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Home</span>
              </Button>
            )}
            
            {/* Reflect → Create Button */}
            <Link to="/reflect">
              <Button
                data-testid="reflect-create-btn"
                variant="outline"
                size="sm"
                className="rounded-full border-[#FF3DA5]/60 text-[#FF3DA5] hover:bg-[#FF3DA5]/10 hover:text-[#FF3DA5] gap-1
                  shadow-[0_0_12px_rgba(255,61,165,0.25)]"
                title="Reflect → Create"
              >
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline">Reflect</span>
              </Button>
            </Link>

            <Link to="/booth">
              <Button
                data-testid="the-booth-btn"
                variant="outline"
                size="sm"
                className="rounded-full border-primary/50 text-primary hover:bg-primary/10 gap-1"
                title="The Booth"
              >
                <Mic2 className="w-4 h-4" />
                <span className="hidden sm:inline">The Booth</span>
              </Button>
            </Link>
            
            {/* Favorites Button */}
            <Button
              data-testid="favorites-btn"
              onClick={() => setShowFavorites(!showFavorites)}
              variant={showFavorites ? "default" : "outline"}
              size="sm"
              className={`rounded-full ${showFavorites ? 'bg-red-500 hover:bg-red-600 text-white' : 'border-red-500/50 text-red-500 hover:bg-red-500/10'}`}
              title="Favorites"
            >
              <Heart className={`w-4 h-4 ${favorites.length > 0 ? 'fill-current' : ''}`} />
              {favorites.length > 0 && (
                <span className="ml-1">{favorites.length}</span>
              )}
            </Button>
            
            {/* Crate Button - Global Header */}
            <CrateHeaderButton />
            <Button aria-pressed={proDetails} onClick={() => { const next = !proDetails; setProDetails(next); try { localStorage.setItem('sp_pro_details', String(next)); } catch (_) {} }} variant="outline" size="sm" className="min-h-11 rounded-full border-white/20">
              <Eye className="mr-1 w-4 h-4" />Pro details
            </Button>
            
            {/* Admin Link */}
            <Link to="/admin/login">
              <Button 
                data-testid="admin-link"
                variant="ghost" 
                size="icon"
                className="rounded-full hover:bg-white/10"
              >
                <Settings className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </header>
      <PizzulCoach
        onSelectGroup={selectCoachGroup}
        onSend={() => window.dispatchEvent(new CustomEvent('sp-send-arranger'))}
      />
      
      {libraryView.view === 'all' ? (
      <div className="max-w-7xl mx-auto px-4 py-6 lg:py-8">
        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
              
              {/* Regular Filters */}
              <Filters
                filters={filters}
                setFilters={setFilters}
                taxonomy={taxonomy}
                onClear={() => clearFilters()}
              />
            </div>
          </aside>
          
          {/* Main Content */}
          {/* pb-24 keeps the fixed preview dock (h-16) from covering the last row of cards */}
          <main className="flex-1 min-w-0 pb-24">
            {returnPack && (
              <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-card/70 p-3" data-testid="return-to-pack">
                {returnPack.cover && <img src={returnPack.cover} alt="" className="h-12 w-12 rounded-xl object-cover" />}
                <p className="min-w-0 flex-1 text-sm sm:text-base">
                  Finding {filters.instrument_group ? filters.instrument_group.toLowerCase() : 'sounds'} for <span className="font-bold">{returnPack.name}</span>
                </p>
                <Button onClick={() => backToPack()} className="neon-btn min-h-11 rounded-full bg-primary px-5 font-bold text-black hover:bg-primary/90" style={{ '--glow': '#00FFDD' }}>
                  <ArrowLeft className="mr-2 h-4 w-4" />Back to {returnPack.name}
                </Button>
              </div>
            )}
            <nav role="tablist" aria-label="Loop instruments" className="mb-6 grid grid-cols-2 gap-2 md:grid-cols-5">
              {[['', 'All'], ...INSTRUMENT_GROUPS.map((group) => [group, group])].map(([group, label]) => {
                const active = filters.instrument_group === group;
                return <button key={label} role="tab" aria-selected={active} onClick={() => setFilters((previous) => ({ ...previous, instrument_group: group }))} className={`min-h-11 rounded-2xl border-2 px-3 font-bold transition-shadow focus-visible:ring-2 focus-visible:ring-white ${active ? 'neon-tab-active bg-white text-black border-white' : 'border-white/15 bg-card'}`} style={group ? (active ? { backgroundColor: GROUP_HEX[group], borderColor: GROUP_HEX[group], color: '#0B0B0C', '--glow': GROUP_HEX[group] } : { borderColor: `${GROUP_HEX[group]}66`, color: GROUP_HEX[group] }) : (active ? { '--glow': '#FFFFFF' } : undefined)}>{label}</button>;
              })}
            </nav>
            <div className="mb-5 flex flex-wrap items-center gap-2" aria-label="Active filters">
              {Number.isInteger(fitsBpm) && <button aria-pressed={fitsMyBeat} onClick={() => { setFitsMyBeat((value) => !value); if (handoffBpm) { setHandoffBpm(null); setHandoffPackName(null); } }} className={`min-h-11 rounded-full border px-4 font-bold ${fitsMyBeat ? 'bg-primary text-black border-primary' : 'border-white/20'}`}>{handoffBpm ? `Fits ${handoffPackName || 'this pack'}: ${fitsBpm} BPM` : `Fits my beat: ${fitsBpm} BPM`}</button>}
              {Object.entries(filters).filter(([key, value]) => key !== 'bpm_range' && key !== 'instrument_group' && value).map(([key, value]) => <button key={key} onClick={() => { setFilters((previous) => ({ ...previous, [key]: '' })); if (key === 'vibe_pack') setActiveVibePack(null); }} className="min-h-11 rounded-full border border-white/20 px-3 text-sm">{String(value)} <X className="ml-1 inline w-3" /></button>)}
              {(fitsMyBeat || Object.entries(filters).some(([key, value]) => key !== 'instrument_group' && value && (!Array.isArray(value) || !isDefaultBpmRange(value)))) && <Button variant="ghost" onClick={() => { clearFilters(); setFitsMyBeat(false); }}>Clear all</Button>}
            </div>
            
            {/* Choose Your Vibe Section
                Shown whenever nothing is playing and no themed pack is open,
                which includes every fresh visit. It used to carry
                `recentLoops.length === 0`, and recents persist in localStorage,
                so any student who had ever played a loop came back to a page of
                Recently Played with no way into the vibes except a Home button
                they had to know to press. For a mood-first library that put the
                front door behind a previous session's history. */}
            {vibePacks.length > 0 && !currentLoop && (
              <section className="mb-6" aria-label="Choose your vibe">
                <div className="mb-2 flex items-baseline gap-2">
                  <h2 className="font-heading text-base font-bold">Choose your vibe</h2>
                  <span className="text-xs text-muted-foreground">Start with a feeling.</span>
                </div>
                {/* One row that scrolls sideways, so the loops start right under it. */}
                <div className="vibe-row -mx-1 flex gap-2 overflow-x-auto px-1 pb-2 pt-1">
                  {vibePacks.map(pack => {
                    const { Icon, color } = vibeStyle(pack.id);
                    const active = activeVibePack?.id === pack.id;
                    return (
                      <button
                        key={pack.id}
                        type="button"
                        data-testid={`vibe-pack-${pack.id}`}
                        aria-pressed={active}
                        onClick={() => applyVibePack(pack)}
                        style={{ '--glow': color }}
                        className={`vibe-chip group inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full border py-1.5 pl-1.5 pr-4 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white ${active ? 'is-active' : ''}`}
                      >
                        <span className="vibe-chip-icon flex h-8 w-8 items-center justify-center rounded-full">
                          <Icon className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
                        </span>
                        {pack.name}
                      </button>
                    );
                  })}
                </div>
              </section>
            )}
            
            {/* Favorites Section - Compact */}
            {showFavorites && (
              <section data-testid="favorites-section" className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-red-500 fill-current" />
                    <h2 className="font-heading font-semibold text-sm">My Favorites</h2>
                    <span className="text-xs text-muted-foreground">({favorites.length})</span>
                  </div>
                  {favorites.length > 0 && (
                    <Button
                      data-testid="clear-favorites-btn"
                      variant="ghost"
                      size="sm"
                      onClick={clearFavorites}
                      className="text-muted-foreground hover:text-white rounded-full h-7 text-xs px-2"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
                {favorites.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {favorites.map(loop => (
                      <button
                        key={`fav-${loop.loop_id}`}
                        onClick={() => playLoop(loop)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/30 hover:border-red-500/50 hover:bg-red-500/20 transition-all text-sm group"
                      >
                        <Heart className="w-3 h-3 text-red-500 fill-current" />
                        <span className="font-medium truncate max-w-[120px]">{loop.title}</span>
                        <span className="text-xs text-muted-foreground">
                          {loop.bpm}
                          {loop.key && loop.instrument_group === 'Melodies' && (
                            <span className="text-violet-400"> • {loop.key}</span>
                          )}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(loop);
                          }}
                          className="opacity-0 group-hover:opacity-100 ml-1 hover:text-red-500 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 rounded-2xl bg-card/30 border border-white/10">
                    <Heart className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-muted-foreground text-sm">
                      Click the heart on any loop to save it here
                    </p>
                  </div>
                )}
              </section>
            )}
            
            {/* Recents Section - Compact */}
            {recentLoops.length > 0 && !currentLoop && !showFavorites && (
              <section className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-accent" />
                    <h2 className="font-heading font-semibold text-sm">Recently Played</h2>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearRecents}
                    className="text-muted-foreground hover:text-white rounded-full h-7 text-xs px-2"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Clear
                  </Button>
                </div>
                <div className="vibe-row flex gap-2 overflow-x-auto pb-1">
                  {recentLoops.slice(0, 6).map(loop => (
                    <button
                      key={`recent-${loop.loop_id}`}
                      onClick={() => playLoop(loop)}
                      className="flex min-h-9 shrink-0 items-center gap-2 px-3 py-1.5 rounded-full bg-card/50 border border-white/10 hover:border-accent/50 hover:bg-accent/10 transition-all text-sm"
                    >
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: GROUP_HEX[loop.instrument_group] || '#FFFFFF' }} aria-hidden="true" />
                      <span className="font-medium truncate max-w-[140px]">{loop.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {loop.bpm}
                        {loop.key && loop.instrument_group === 'Melodies' && (
                          <span className="text-violet-400"> • {loop.key}</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            )}
            
            {/* Suggestions Section - Compact */}
            {suggestions.length > 0 && (
              <section className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <h2 className="font-heading font-semibold text-sm">Suggested for your mood</h2>
                </div>
                <div className="vibe-row flex gap-2 overflow-x-auto pb-1">
                  {suggestions.slice(0, 6).map(loop => (
                    <button
                      key={`suggest-${loop.loop_id}`}
                      onClick={() => playLoop(loop)}
                      className="flex min-h-9 shrink-0 items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/30 hover:border-primary/50 hover:bg-primary/20 transition-all text-sm"
                    >
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: GROUP_HEX[loop.instrument_group] || '#FFFFFF' }} aria-hidden="true" />
                      <span className="font-medium truncate max-w-[140px]">{loop.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {loop.bpm}
                        {loop.key && loop.instrument_group === 'Melodies' && (
                          <span className="text-violet-400"> • {loop.key}</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            )}
            
            {/* Loop Grid */}
            <section>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading font-bold text-lg">
                      {filters.mood_category || 'All Loops'}
                    </h2>
                    <span className="text-sm text-muted-foreground">
                      {loops.length} {loops.length === 1 ? 'loop' : 'loops'}
                    </span>
                  </div>
                  
                  {loopError ? (
                    <div role="status" className="rounded-2xl border border-red-500/50 p-5">Couldn't load loops. <Button variant="link" onClick={() => setFilters((value) => ({ ...value }))}>Try again</Button></div>
                  ) : loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 auto-rows-fr gap-4">
                      {[...Array(6)].map((_, i) => (
                        <div 
                          key={i} 
                          className="rounded-3xl bg-card/50 border border-white/10 h-52 animate-pulse"
                        />
                      ))}
                    </div>
                  ) : loops.length === 0 ? (
                    <div className="text-center py-16">
                      <img 
                        src={PIZZLE_IMAGE} 
                        alt="Pizzul" 
                        className="w-32 h-32 mx-auto mb-4 opacity-50"
                      />
                      <h3 className="font-heading font-bold text-xl mb-2">{fitsMyBeat && Number.isInteger(fitsBpm) ? `No ${filters.instrument_group ? filters.instrument_group.toLowerCase() : 'loops'} fit your ${fitsBpm} BPM beat yet.` : 'No loops found'}</h3>
                      <p className="text-muted-foreground mb-4">
                        Try adjusting your filters or search terms
                      </p>
                      <Button
                        onClick={() => fitsMyBeat ? setFitsMyBeat(false) : clearFilters()}
                        variant="outline"
                        className="rounded-full border-white/20"
                      >
                        {fitsMyBeat ? 'Show all tempos' : 'Clear filters'}
                      </Button>
                    </div>
                  ) : (
                    <div 
                      data-testid="loops-grid"
                      className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 auto-rows-fr gap-4"
                    >
                      {loops.map((loop, index) => (
                        <div 
                          key={loop.loop_id}
                          className="animate-fade-in"
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                          <LoopCard 
                            loop={loop}
                            proDetails={proDetails}
                            isFavorite={isFavorite(loop.loop_id)}
                            onToggleFavorite={toggleFavorite}
                          />
                        </div>
                      ))}
                    </div>
                  )}
              
              {/* Load More Button - only show when not in themed pack mode */}
              {!loading && loops.length > 0 && hasMore && (
                <div className="text-center mt-8">
                  <Button
                    data-testid="load-more-btn"
                    onClick={loadMoreLoops}
                    variant="outline"
                    className="rounded-full border-white/20 px-8"
                  >
                    Load More
                  </Button>
                </div>
              )}
              {partialLoopError && <div role="status" className="mt-4 text-center text-sm text-muted-foreground">Some loops couldn't load. <Button variant="link" onClick={loadMoreLoops}>Try again</Button></div>}
              
              {/* Loop count */}
              {!loading && loops.length > 0 && (
                <div className="text-center mt-4">
                  <span className="text-sm text-muted-foreground">
                    Showing {loops.length} loops {!hasMore && '(all loaded)'}
                  </span>
                </div>
              )}
            </section>
          </main>
        </div>
      </div>
      ) : (
        <PacksLibrary
          mode={libraryView.view}
          activePack={libraryView.pack}
          onOpenPack={openPack}
          onBack={() => setLibraryView('packs')}
          onAllLoops={handoffToAllLoops}
          selectedGroup={libraryView.packTab}
          onSelectedGroupChange={(tab) => dispatchLibraryView({ type: 'PACK_TAB', tab })}
          player={{ stop, playLoop, currentLoop, isPlaying }}
          favorites={{ isFavorite, toggleFavorite }}
          proDetails={proDetails}
        />
      )}
      
      
      {/* Footer */}
      <footer className="border-t border-white/10 py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">
            Studio Playground 2.0 by <span className="text-primary font-medium">Let The Beat Build</span> | A mood-first music creation tool for learning and expression.
          </p>
        </div>
      </footer>
      
      {/* Bottom padding for preview dock */}
      <div className="h-20" />
    </div>
  );
};

export default Library;
